
import { Subscription } from "@/types/subscription";

/**
 * Safely processes raw JSON/metadata from Supabase to avoid TypeScript errors
 */
function safelyParseMetadata(metadata: any): Record<string, any> {
  // If metadata is already an object, return it
  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    return metadata;
  }
  
  // If metadata is a string, try to parse it
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (e) {
      console.warn("Failed to parse metadata string:", e);
    }
  }
  
  // Default fallback
  return {};
}

/**
 * Processes raw subscription data from Supabase to match the expected Subscription type
 * by adding computed properties and safely handling metadata fields.
 */
export function processSubscriptionData(rawData: any): Subscription {
  if (!rawData) {
    return {} as Subscription;
  }
  
  // Safely process the metadata
  const metadata = safelyParseMetadata(rawData.metadata);
  
  // Create the processed subscription with all required fields
  const processedSubscription: Subscription = {
    ...rawData,
    // Ensure these fields are properly typed as booleans
    hasActiveSubscription: rawData.status === 'active',
    isInTrialPeriod: 
      rawData.trial_started_at && 
      rawData.trial_ends_at && 
      new Date(rawData.trial_ends_at) > new Date() && 
      !rawData.stripe_subscription_id,
    hasPaymentIssues: rawData.has_payment_issues === true,
    isAnnual: rawData.is_annual === true,
    // Set metadata as processed object
    metadata: {
      ...metadata,
      custom_resolutions: metadata.custom_resolutions,
      custom_person_resolutions: metadata.custom_person_resolutions,
      pending_downgrade: metadata.pending_downgrade === true,
      next_plan: metadata.next_plan,
      downgrade_effective_date: metadata.downgrade_effective_date,
      isAnnual: metadata.isAnnual === true || metadata.billing_cycle === 'annual',
      billing_cycle: metadata.billing_cycle
    }
  };
  
  // Also add metadata properties directly to the subscription object for backward compatibility
  if (metadata.pending_downgrade !== undefined) {
    processedSubscription.pending_downgrade = metadata.pending_downgrade === true;
  }
  
  if (metadata.next_plan !== undefined) {
    processedSubscription.next_plan = metadata.next_plan;
  }
  
  if (metadata.downgrade_effective_date !== undefined) {
    processedSubscription.downgrade_effective_date = metadata.downgrade_effective_date;
  }
  
  return processedSubscription;
}

/**
 * Safely accesses metadata properties from a subscription object
 */
export function getMetadataValue(subscription: Subscription | null | undefined, key: string): any {
  if (!subscription || !subscription.metadata) return undefined;
  
  // Use the safely parsed metadata directly
  const metadata = subscription.metadata;
  return metadata[key];
}

/**
 * Format the end date of a subscription to a human-readable string
 */
export function formatEndDate(subscription: any): string {
  if (!subscription?.current_period_end) return '';
  return new Date(subscription.current_period_end).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
